import { TriangleAlert } from 'lucide-react';

import type { PreviewNote } from '../lib/draft-to-gap';

/**
 * What the page below is not.
 *
 * The preview renders the customer-facing component unmodified — no "from run"
 * chips are injected into its markup, because that would mean editing customer
 * components for a developer tool. So the marking the two-zone rule requires
 * lands here instead: outside the app's own chrome, above it, naming every field
 * that is a run fallback and every one the app is rendering without.
 *
 * A reviewer who reads only the page below and not this strip would take run
 * prose for enriched output. That is the mistake this exists to prevent.
 */
export function PreviewFallbackBanner({
  fromRun,
  missing,
}: {
  fromRun: PreviewNote[];
  missing: PreviewNote[];
}) {
  if (fromRun.length === 0 && missing.length === 0) {
    return (
      <div className="gr-appnote">
        <div className="gr-note" data-tone="cyan">
          <div>
            <strong>Every field on this page is enriched output.</strong>{' '}
            Nothing below is standing in from the run — this is what the
            customer would be served.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gr-appnote">
      <div className="gr-note">
        <TriangleAlert />
        <div>
          <strong>This is not the final output.</strong> The draft has not been
          enriched yet, so the page below is part run prose and part missing
          sections. Publishing to <code>gaps</code> still requires the real
          enriched gap.
          {fromRun.length > 0 && (
            <>
              <div className="gr-notelist-h">
                Rendered, but taken from the run
              </div>
              <ul className="gr-notelist">
                {fromRun.map((note) => (
                  <li key={note.field}>
                    <code>gap.{note.field}</code> — {note.detail}
                  </li>
                ))}
              </ul>
            </>
          )}
          {missing.length > 0 && (
            <>
              <div className="gr-notelist-h">
                Not on the page at all — the app omits the section
              </div>
              <ul className="gr-notelist">
                {missing.map((note) => (
                  <li key={note.field}>
                    <code>gap.{note.field}</code> — {note.detail}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
